import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiErrorDetail } from '../api';
import { formatPts } from '../lib/format';
import { useAuth } from '../context/AuthContext';

/**
 * FeatureClanSheet — creator pays Points to pin their Clan to Home Top-5.
 */
export default function FeatureClanSheet({ onClose, onPurchased }) {
  const { user } = useAuth();
  const isVerified = !!(user && user.verified);
  const [tiers, setTiers] = useState([]);
  const [current, setCurrent] = useState(null);
  const [balance, setBalance] = useState(0);
  const [picked, setPicked] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/featured/pricing').catch(() => ({ data: { tiers: [] } })),
      api.get('/featured/mine').catch(() => ({ data: { slot: null } })),
      api.get('/wallet/balance').catch(() => ({ data: { balance: 0 } })),
    ]).then(([p, m, b]) => {
      setTiers(p.data.tiers || []);
      setCurrent(m.data.slot);
      setBalance(b.data.balance || 0);
    });
  }, []);

  const chosen = tiers.find((t) => t.days === picked);
  const canBuy = chosen && balance >= chosen.pts && !submitting;

  const buy = async () => {
    if (!canBuy) return;
    setSubmitting(true);
    setErr('');
    try {
      const { data } = await api.post('/featured/purchase', { days: picked });
      onPurchased(data.slot);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not purchase.');
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-scrim" onClick={onClose}>
      <div className="cs-sheet" onClick={(e) => e.stopPropagation()} data-testid="feature-sheet">
        <div className="cs-head">
          <div className="cs-title">⭐ Get Featured</div>
          <button className="cs-close" onClick={onClose} data-testid="feature-close">×</button>
        </div>
        <p className="cs-sub">
          Pin your Clan to the Home Top-5 slot. Highest ROI channel for growing holders.
        </p>

        {!isVerified && (
          <div className="feature-gate" data-testid="feature-verify-gate">
            <div className="feature-gate-emoji">✓</div>
            <div className="feature-gate-title">Verified creators only</div>
            <p className="feature-gate-body">
              The Featured slot is a trust signal — only creators with a ✓ verified badge can pay
              for it. Get your badge first (it's free) and this unlocks instantly.
            </p>
            <Link to="/verify" className="cs-btn-primary" data-testid="feature-gate-cta" onClick={onClose}>
              Get verified →
            </Link>
          </div>
        )}

        {isVerified && current && (
          <div className="cs-quote" data-testid="feature-current" style={{ marginBottom: 16 }}>
            <div className="cs-quote-row">
              <span>Currently featured</span>
              <b>{current.remaining_hours}h left</b>
            </div>
            <div className="cs-quote-row">
              <span>Total spent</span>
              <b>{current.pts_paid} pts</b>
            </div>
            <div className="cs-hint">Buying again extends the expiry.</div>
          </div>
        )}

        {isVerified && (
        <>
        <div className="cs-field">
          <label className="cs-label">Pick a plan</label>
          <div className="feature-tier-list" data-testid="feature-tier-list">
            {tiers.map((t) => (
              <button
                key={t.days}
                type="button"
                className={`feature-tier${picked === t.days ? ' selected' : ''}`}
                onClick={() => setPicked(t.days)}
                data-testid={`feature-tier-${t.days}`}
              >
                <div className="ft-head">
                  <div className="ft-label">{t.label}</div>
                  <div className="ft-price">{t.pts} pts</div>
                </div>
                <div className="ft-desc">{t.description}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="cs-quote">
          <div className="cs-quote-row"><span>Balance</span><b>{formatPts(balance, 2)} pts</b></div>
          <div className="cs-quote-row"><span>Total to pay</span><b>{chosen ? chosen.pts : 0} pts</b></div>
        </div>

        {chosen && balance < chosen.pts && (
          <div className="cs-err" data-testid="feature-err">
            Not enough Points — you need {(chosen.pts - balance).toFixed(2)} more to buy this tier.
          </div>
        )}
        {err && <div className="cs-err" data-testid="feature-err">{err}</div>}

        <button
          className="cs-btn-primary"
          disabled={!canBuy}
          onClick={buy}
          data-testid="feature-buy-btn"
        >
          {submitting ? 'Purchasing…' : chosen ? `Feature for ${chosen.label} · ${chosen.pts} pts` : 'Pick a plan'}
        </button>
        <p className="cs-fine">
          No refunds. Cancel anytime — you'll lose the remaining time.
        </p>
        </>
        )}
      </div>
    </div>
  );
}
